import { cn } from "@/utils";
import type { FileRecord } from "@cohesive/api-client";
import { Folder, FolderOpen } from "lucide-react";
import { useState } from "react";

/**
 * Shared folder-tree utilities and UI for folder-picker dialogs
 * (e.g. `MoveFileRecordDialog`, `BulkMoveDialog`). Keeping one
 * implementation here prevents the tree semantics — folder sort,
 * parent lookup, orphan handling, expand/collapse, forbidden paths —
 * from drifting between callers.
 */

export interface FolderNode {
  path: string;
  name: string;
  children: FolderNode[];
}

/**
 * Build a hierarchical folder tree from a flat list of records.
 * Only records whose path ends in "/" are treated as folders.
 * Folders whose parent is missing from the input are surfaced at the
 * root so users can still see and target them.
 */
export function buildFolderTree(records: FileRecord[]): FolderNode[] {
  const folders = records.filter((r) => r.path.endsWith("/"));
  const rootFolders: FolderNode[] = [];

  const sorted = [...folders].sort((a, b) => a.path.localeCompare(b.path));

  for (const folder of sorted) {
    const cleaned = folder.path.endsWith("/")
      ? folder.path.slice(0, -1)
      : folder.path;
    const segments = cleaned.split("/").filter(Boolean);
    const name = segments[segments.length - 1] || folder.path;

    const parentPath =
      segments.length > 1 ? segments.slice(0, -1).join("/") + "/" : "";

    const node: FolderNode = { path: folder.path, name, children: [] };

    if (!parentPath) {
      rootFolders.push(node);
    } else {
      const parent = findNode(rootFolders, parentPath);
      if (parent) {
        parent.children.push(node);
      } else {
        rootFolders.push(node);
      }
    }
  }

  return rootFolders;
}

/** Depth-first search for a folder node by its full path. */
export function findNode(nodes: FolderNode[], path: string): FolderNode | null {
  for (const node of nodes) {
    if (node.path === path) return node;
    const found = findNode(node.children, path);
    if (found) return found;
  }
  return null;
}

interface FolderTreeItemProps {
  node: FolderNode;
  selected: string;
  onSelect: (path: string) => void;
  /**
   * Set of folder paths that can't be selected. Any node whose path
   * equals — or is a descendant of — a forbidden path is rendered
   * disabled. Used by move dialogs to prevent moving a folder into
   * itself or one of its children.
   */
  forbidden?: Set<string>;
  depth?: number;
}

/**
 * Recursive row renderer for the folder tree. Auto-expands the first
 * two depths so the common case (picking a top-level destination)
 * doesn't require extra clicks.
 */
export function FolderTreeItem({
  node,
  selected,
  onSelect,
  forbidden,
  depth = 0,
}: FolderTreeItemProps) {
  const [expanded, setExpanded] = useState(depth < 2);
  const isSelected = selected === node.path;
  const hasChildren = node.children.length > 0;
  const disabled = forbidden
    ? isDescendantOfForbidden(node.path, forbidden)
    : false;
  const Icon = expanded && hasChildren ? FolderOpen : Folder;

  return (
    <div>
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          onSelect(node.path);
          if (hasChildren) setExpanded((e) => !e);
        }}
        className={cn(
          "w-full flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm transition-colors",
          disabled
            ? "text-muted-foreground/50 cursor-not-allowed"
            : isSelected
              ? "bg-foreground/10 text-foreground font-medium"
              : "text-muted-foreground hover:bg-muted hover:text-foreground",
        )}
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
      >
        <Icon className="h-4 w-4 shrink-0" />
        <span className="truncate">{node.name}</span>
      </button>
      {expanded &&
        node.children.map((child) => (
          <FolderTreeItem
            key={child.path}
            node={child}
            selected={selected}
            onSelect={onSelect}
            forbidden={forbidden}
            depth={depth + 1}
          />
        ))}
    </div>
  );
}

function isDescendantOfForbidden(
  path: string,
  forbidden: Set<string>,
): boolean {
  for (const f of forbidden) {
    if (path === f) return true;
    if (path.startsWith(f)) return true;
  }
  return false;
}
